import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { X, ArrowRight, ArrowLeft, Check } from "lucide-react";
import StepProdutos from "./step-produtos";
import StepPagamento from "./step-pagamento";
import Notification from "../../../../../components/toast";
import reqVenda from "../../../../../services/reqMultiTenant/reqVenda";
import { validarParcelas, validarReconciliacao, validarSaldoParaAbatimento } from "./_utils/parcelas-rules";

export default function ModalAlteracaoVenda({
	isOpen,
	onClose,
	venda,
	originalItens,
	originalComplementos,
	originalParcelas,
	onSaved,
}) {
	const [currentStep, setCurrentStep] = useState(1);
	const [itens, setItens] = useState(originalItens || []);
	const [complementos, setComplementos] = useState(originalComplementos || []);
	const [totalVenda, setTotalVenda] = useState(Number(venda?.valor || 0));
	const [parcelas, setParcelas] = useState([]);
	const [tipoOperacao, setTipoOperacao] = useState("CRIAR MAIS PARCELAS");
	const [isAddingProduct, setIsAddingProduct] = useState(false);
	const [salvando, setSalvando] = useState(false);

	// useState só captura os props na primeira montagem — sem isso, reabrir o wizard (mesmo
	// componente, só isOpen mudando) mostraria dado da sessão anterior em vez do retrato
	// atual do banco que handleAbrirAlteracaoGeral acabou de buscar.
	useEffect(() => {
		if (isOpen) {
			setCurrentStep(1);
			setItens(originalItens || []);
			setComplementos(originalComplementos || []);
			setTotalVenda(Number(venda?.valor || 0));
			setParcelas([]);
			setTipoOperacao("CRIAR MAIS PARCELAS");
			setIsAddingProduct(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	// Só avança pra Formas de Pagamento se algo realmente mudou nos produtos (adicionou ou
	// removeu item, ou alterou quantidade de um complemento de grade) e se essa mudança
	// alterou o valor total da venda — sem isso não há motivo pra reestruturar as parcelas
	// (pra só refazer parcelas sem mexer em produto, já existe o fluxo isolado "Refazer Parcelas").
	// Verifica se houve alguma mudança REAL de produtos, impedindo que flutuações
	// que voltaram ao valor original (ex: qtde 1 -> 2 -> 1) sejam contadas como alteração.
	const checarAlteracaoReal = () => {
		// 1. Itens novos (não removidos) ou originais removidos
		if (itens.some((i) => i._novo && !i._removido)) return true;
		if (itens.some((i) => !i._novo && i._removido)) return true;

		// 2. Complementos novos (não removidos) ou originais removidos
		if ((complementos || []).some((c) => c._novo && !c._removido)) return true;
		if ((complementos || []).some((c) => !c._novo && c._removido)) return true;

		// 3. Complementos originais com quantidade alterada
		const compModificado = (complementos || []).some((c) => {
			if (c._novo || c._removido) return false;
			const original = (originalComplementos || []).find(
				(oc) => oc.id_venda_complementos === c.id_venda_complementos,
			);
			if (!original) return true; // Fallback se não achar
			return Number(c.qtde ?? c.quantidade ?? 1) !== Number(original.qtde ?? original.quantidade ?? 1);
		});
		if (compModificado) return true;

		return false;
	};

	const houveAlteracaoProdutos = checarAlteracaoReal();

	const handleNext = () => {
		if (!houveAlteracaoProdutos) {
			Notification.showToast({ message: "Nenhuma alteração foi feita nos produtos.", type: "warning" });
			return;
		}

		const totalOriginal = Number(venda?.valor || 0);
		const houveAlteracaoValor = Math.abs(totalVenda - totalOriginal) > 0.005;

		if (!houveAlteracaoValor) {
			// Não deve cair aqui se a interface trocar o botão corretamente, mas por segurança:
			setTipoOperacao("MANTER PARCELAS");
			return;
		}

		if (totalVenda > totalOriginal) {
			setTipoOperacao("CRIAR MAIS PARCELAS");
		} else {
			setTipoOperacao("REFAZER PARCELAMENTO");
		}

		// Limpa parcelas de uma passada anterior pelo Passo 2 — sem isso, se o usuário volta,
		// altera produtos de novo (mudando a diferença) e avança de novo mantendo a mesma
		// operação, o useEffect de geração em step-pagamento.jsx só inicializa quando
		// parcelas.length === 0, e ficaria mostrando o valor antigo da diferença.
		setParcelas([]);

		setCurrentStep(2);
	};

	const handleBack = () => {
		setCurrentStep(1);
	};

	// Monta o payload final da alteração — o front só organiza, quem grava é o backend.
	// Manda apenas o que mudou + o resultado financeiro escolhido.
	//
	// Estrutura que o backend (DAO.AlteracaoVenda) espera:
	//   itens[]                → itens INSERIR (com seus complementos aninhados) ou EXCLUIR
	//   complementos_alterados → só complementos de grade que tiveram a qtde mudada (já têm id real)
	// Complemento de item novo NÃO vai na lista separada: o id_venda_item real só nasce no
	// INSERT do item, então ele viaja aninhado dentro do próprio item.
	const montarPayload = (operacaoOverride) => {
		const totalOriginal = Number(venda?.valor || 0);
		const diferenca = totalVenda - totalOriginal;

		const complementosDoItem = (idVendaItem) =>
			complementos
				.filter((c) => c.id_venda_item === idVendaItem)
				.map((c) => ({
					id_produto: c.id_produto,
					nome: c.nome,
					qtde: c.qtde,
					valorunit: c.valorunit,
					valor: c.valor,
					tipo: c.tipo || null,
					obs: c.obs || null,
				}));

		const idsItensExcluidos = itens.filter((i) => i._removido).map((i) => i.id_venda_item);

		return {
			id_caixa: 1, // caixa fixo em 1 (regra definida pelo usuário)
			tipo_operacao: operacaoOverride || tipoOperacao,
			venda: {
				id_venda: venda?.id_venda,
				valor_original: totalOriginal,
				valor_novo: totalVenda,
				diferenca,
			},
			itens: itens
				.filter((i) => i._novo || i._removido)
				.map((i) =>
					i._novo
						? {
								acao: "INSERIR",
								id_produto: i.id_produto,
								qtde: i.qtde,
								valor_unit: i.valor_unitario,
								valor: i.valor,
								desconto: i.desconto || 0,
								grade: i.grade || null,
								obs: i.obs || null,
								complementos: complementosDoItem(i.id_venda_item),
							}
						: {
								acao: "EXCLUIR",
								id_venda_item: i.id_venda_item,
							},
				),
			// Só complementos de itens que já existiam no banco, tiveram qtde alterada (grade) ou foram removidos
			// isoladamente (se o pai inteiro for removido, o Delphi já cuida dos filhos em cascata)
			complementos_alterados: complementos
				.filter(
					(c) =>
						(c._qtde_alterada || c._removido) && !c._novo && !idsItensExcluidos.includes(c.id_venda_item),
				)
				.map((c) =>
					c._removido
						? {
								acao: "EXCLUIR",
								id_venda_complementos: c.id_venda_complementos,
							}
						: {
								id_venda_complementos: c.id_venda_complementos,
								qtde: c.qtde,
								valorunit: c.valorunit,
								valor: c.valor,
								obs: c.obs || null,
							},
				),
			parcelas: parcelas.map((p) => ({
				parcela: p.parcela,
				valor: Number(p.valor || 0),
				id_forma: p.id_forma,
				forma: p.forma || p.forma_pagamento,
				id_conta: p.id_conta,
				data: p.data || p.data_vencimento,
				ativo: p.ativo,
			})),
		};
	};

	const handleConfirm = (isManterParcelas = false) => {
		if (!houveAlteracaoProdutos) {
			Notification.showToast({ message: "Nenhuma alteração foi feita nos produtos.", type: "warning" });
			return;
		}

		const operacaoEfetiva = isManterParcelas === true ? "MANTER PARCELAS" : tipoOperacao;
		const valorFaturar =
			operacaoEfetiva === "REFAZER PARCELAMENTO" ? totalVenda : Math.abs(totalVenda - Number(venda?.valor || 0));

		// Abatimento parcial não gera parcela — valida se existe saldo em aberto suficiente
		// (mesma trava do legado em baixaparcial: 'Valor Insuficiente Para Realizar Operação!!!')
		if (operacaoEfetiva === "ABATER PARCIALMENTE") {
			const erroSaldo = validarSaldoParaAbatimento(originalParcelas, valorFaturar);
			if (erroSaldo) {
				console.warn("[AlteracaoVenda:FRONT] Bloqueado (saldo):", erroSaldo);
				Notification.showToast({ message: erroSaldo, type: "warning" });
				return;
			}
		} else if (operacaoEfetiva === "MANTER PARCELAS") {
			// Manter parcelas não gera novas parcelas nem abate saldo, apenas salva os itens
		} else {
			// Demais operações geram parcelas: valida cada uma e reconcilia a soma
			const erroParcelas = validarParcelas(parcelas);
			if (erroParcelas) {
				console.warn("[AlteracaoVenda:FRONT] Bloqueado (parcelas):", erroParcelas);
				Notification.showToast({ message: erroParcelas, type: "warning" });
				return;
			}

			const erroReconciliacao = validarReconciliacao(parcelas, valorFaturar);
			if (erroReconciliacao) {
				Notification.showToast({ message: erroReconciliacao, type: "warning" });
				return;
			}
		}

		enviarAlteracao(operacaoEfetiva);
	};

	// Envia a reestruturação pro backend (transação única no servidor). No sucesso, delega ao
	// pai (onSaved) recarregar os dados reais e fechar o modal.
	const enviarAlteracao = async (operacaoOverride) => {
		setSalvando(true);
		try {
			const payload = montarPayload(operacaoOverride);

			const resp = await reqVenda.alteracaoGeral(venda?.id_venda, payload);

			Notification.showToast({ message: "Venda alterada com sucesso!", type: "success" });
			if (onSaved) await onSaved();
		} catch (error) {
			const msgErro =
				error?.response?.data || error?.message || "Erro ao salvar a alteração da venda no servidor.";

			Notification.showToast({ message: msgErro, type: "error" });
		} finally {
			setSalvando(false);
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			onRequestClose={onClose}
			overlayClassName="react-modal-overlay"
			className="react-modal-content-base w-full max-w-[950px] h-[90vh] bg-white p-6 rounded-[28px] shadow-2xl relative flex flex-col justify-between"
			shouldCloseOnOverlayClick={false}
			ariaHideApp={false}
		>
			{/* Botão de Fechar */}
			<button
				onClick={onClose}
				className="absolute right-6 top-6 text-gray-400 hover:text-gray-600 transition-colors"
			>
				<X size={20} />
			</button>

			{/* Cabeçalho do Modal e Indicador de Passos */}
			<div className="border-b border-gray-100 pb-4">
				<h2 className="text-xl font-black text-gray-800 tracking-tight">Alteração Geral da Venda</h2>
				<p className="text-xs text-gray-500 font-medium">
					Ajuste os itens e reestruture as formas de pagamento
				</p>

				{/* Linha de Progresso do Stepper */}
				<div className="flex items-center gap-4 mt-4">
					<div className="flex items-center gap-2">
						<span
							className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
								currentStep === 1 ? "bg-blue-600 text-white" : "bg-emerald-100 text-emerald-700"
							}`}
						>
							{currentStep > 1 ? <Check size={14} /> : "1"}
						</span>
						<span className={`text-xs font-bold ${currentStep === 1 ? "text-blue-600" : "text-gray-500"}`}>
							Produtos / Itens
						</span>
					</div>
					<div className="w-12 h-[2px] bg-gray-200"></div>
					<div className="flex items-center gap-2">
						<span
							className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
								currentStep === 2 ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-400"
							}`}
						>
							"2"
						</span>
						<span className={`text-xs font-bold ${currentStep === 2 ? "text-blue-600" : "text-gray-400"}`}>
							Formas de Pagamento
						</span>
					</div>
				</div>
			</div>

			{/* Conteúdo Dinâmico com base no Passo Atual */}
			<div className="flex-1 overflow-y-auto overflow-x-hidden py-6">
				{currentStep === 1 ? (
					<StepProdutos
						itens={itens}
						setItens={setItens}
						complementos={complementos}
						setComplementos={setComplementos}
						totalVenda={totalVenda}
						setTotalVenda={setTotalVenda}
						onAddingProductChange={setIsAddingProduct}
						originalItens={originalItens}
						originalComplementos={originalComplementos}
					/>
				) : (
					<StepPagamento
						totalAParcelar={totalVenda}
						totalOriginal={Number(venda?.valor || 0)}
						originalParcelas={originalParcelas}
						parcelas={parcelas}
						setParcelas={setParcelas}
						tipoOperacao={tipoOperacao}
						setTipoOperacao={setTipoOperacao}
						itens={itens}
						complementos={complementos}
						venda={venda}
					/>
				)}
			</div>

			{/* Rodapé de Ações de Navegação — Oculto quando configurando complementos ou adicionando produto */}
			{!isAddingProduct && (
				<div className="border-t border-gray-100 pt-4 flex justify-between">
					<button
						onClick={onClose}
						disabled={salvando}
						className="h-11 px-6 border border-gray-200 rounded-[20px] text-xs font-bold text-gray-500 hover:bg-gray-50 transition-all disabled:opacity-50"
					>
						Cancelar
					</button>

					<div className="flex gap-2">
						{currentStep === 2 && (
							<button
								onClick={handleBack}
								disabled={salvando}
								className="h-11 px-6 border border-blue-600 text-blue-600 rounded-[20px] text-xs font-bold hover:bg-blue-50 transition-all flex items-center gap-1.5 disabled:opacity-50"
							>
								<ArrowLeft size={16} /> Voltar
							</button>
						)}

						{currentStep === 1 ? (
							houveAlteracaoProdutos && Math.abs(totalVenda - Number(venda?.valor || 0)) <= 0.005 ? (
								<button
									onClick={() => handleConfirm(true)}
									disabled={salvando}
									className="h-11 px-6 bg-emerald-600 hover:bg-emerald-700 text-white rounded-[20px] text-xs font-black uppercase transition-all flex items-center gap-1.5 shadow-md shadow-emerald-600/10 disabled:opacity-60 disabled:cursor-not-allowed"
								>
									{salvando ? "Salvando..." : "Confirmar e Salvar"} <Check size={16} />
								</button>
							) : (
								<button
									onClick={handleNext}
									className="h-11 px-6 bg-blue-600 hover:bg-blue-700 text-white rounded-[20px] text-xs font-bold transition-all flex items-center gap-1.5 shadow-md shadow-blue-600/10"
								>
									Avançar <ArrowRight size={16} />
								</button>
							)
						) : (
							<button
								onClick={() => handleConfirm(false)}
								disabled={salvando}
								className="h-11 px-6 bg-emerald-600 hover:bg-emerald-700 text-white rounded-[20px] text-xs font-black uppercase transition-all flex items-center gap-1.5 shadow-md shadow-emerald-600/10 disabled:opacity-60 disabled:cursor-not-allowed"
							>
								{salvando ? "Salvando..." : "Confirmar e Salvar"} <Check size={16} />
							</button>
						)}
					</div>
				</div>
			)}
		</Modal>
	);
}
